/**
 * API module for puzzle-related queries.
 * 
 * This module provides functions to interact with puzzle endpoints:
 * - Get puzzle instance IDs
 * 
 * All functions handle errors gracefully and return structured responses.
 */

import apiClient from './client';
import { getUserId } from '../analytics';

/**
 * Fetch puzzle instance IDs for multiple games and dates.
 * 
 * This is used on app load to batch-fetch puzzle instances which are then cached
 * for use when creating sessions.
 * 
 * @param {object} options - Query options
 * @param {string[]|null} options.gameSlugs - Game slugs to filter by (null = all games)
 * @param {string|null} options.startDate - Start date YYYY-MM-DD (inclusive)
 * @param {string|null} options.endDate - End date YYYY-MM-DD (inclusive)
 * @param {boolean} options.includeInactive - If true, also returns inactive/replaced instances (for admin views)
 * @param {string|null} token - Optional Clerk JWT token for authentication
 * @returns {Promise<{
 *   success: boolean,
 *   instances?: Array<{
 *     game_slug: string,
 *     puzzle_date: string,
 *     puzzle_instance_id: string,
 *     puzzle_id: string,
 *     version: number,
 *     is_active: boolean,
 *     user_session?: {puzzle_instance_id: string, status: string, version: number} | null,
 *     has_newer_version: boolean,
 *     user_has_played: boolean,
 *     session_count: number
 *   }>,
 *   error?: string,
 *   message?: string
 * }>}
 * 
 * @example
 * // Get puzzle instances for all games in the past 30 days
 * const thirtyDaysAgo = new Date();
 * thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
 * const result = await getPuzzleInstances({
 *   startDate: thirtyDaysAgo.toISOString().split('T')[0],
 *   endDate: new Date().toISOString().split('T')[0],
 * }, clerkToken);
 */
export async function getPuzzleInstances(options = {}, token = null, signal = null) {
  try {
    const params = new URLSearchParams();
    
    if (options.gameSlugs && options.gameSlugs.length > 0) {
      params.append('game_slugs', options.gameSlugs.join(','));
    }
    if (options.startDate) {
      params.append('start_date', options.startDate);
    }
    if (options.endDate) {
      params.append('end_date', options.endDate);
    }
    if (options.includeInactive) {
      params.append('include_inactive', 'true');
    }
    
    const queryString = params.toString();
    const url = queryString ? `/puzzles/instances?${queryString}` : '/puzzles/instances';
    
    const headers = {};
    const uid = getUserId();
    if (uid) headers['X-User-ID'] = uid;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    const requestOptions = { headers };
    if (signal) requestOptions.signal = signal;
    
    const response = await apiClient.get(url, requestOptions);
    return response.data;
  } catch (error) {
    // Re-throw abort errors so they can be handled by the caller
    if (error.name === 'AbortError' || error.name === 'CanceledError') {
      throw error;
    }
    console.error('Error fetching puzzle instances:', error);
    if (error.response?.data) {
      return error.response.data;
    }
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: 'Errore di connessione'
    };
  }
}

/**
 * Fetch the puzzle catalog for admin management.
 * 
 * @param {object} options - Query options
 * @param {string[]|null} options.gameSlugs - Array of game slugs to filter by (null = all games)
 * @param {string|null} options.search - Text search over config
 * @param {string|null} options.scheduledAfter - Filter by date
 * @param {string|null} options.scheduledBefore - Filter by date
 * @param {string} options.scheduleStatus - Filter by schedule status: 'all', 'scheduled', 'unscheduled'
 * @param {string} options.orderBy - Order by field: 'created_at' or 'scheduled_date'
 * @param {string} options.orderDirection - Sort direction: 'asc' or 'desc'
 * @param {number} options.limit - Pagination limit
 * @param {number} options.offset - Pagination offset
 * @param {string} token - Clerk JWT token (required)
 */
export async function getPuzzleCatalog(options = {}, token) {
  try {
    const params = new URLSearchParams();
    if (options.gameSlugs && options.gameSlugs.length > 0) {
      params.append('game_slugs', options.gameSlugs.join(','));
    }
    if (options.search) params.append('search', options.search);
    if (options.scheduledAfter) params.append('scheduled_after', options.scheduledAfter);
    if (options.scheduledBefore) params.append('scheduled_before', options.scheduledBefore);
    if (options.scheduleStatus) params.append('schedule_status', options.scheduleStatus);
    if (options.orderBy) params.append('order_by', options.orderBy);
    if (options.orderDirection) params.append('order_direction', options.orderDirection);
    if (options.limit) params.append('limit', options.limit);
    if (options.offset) params.append('offset', options.offset);

    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.get(`/puzzles?${params.toString()}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching puzzle catalog:', error);
    if (error.response?.data) return error.response.data;
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Fetch a puzzle by its ID.
 *
 * @param {string} puzzleId - Puzzle version UUID
 * @param {string|null} token - Optional Clerk JWT token for authentication
 */
export async function getPuzzleById(puzzleId, token = null) {
  try {
    const headers = {};
    const uid = getUserId();
    if (uid) headers['X-User-ID'] = uid;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await apiClient.get(`/puzzles/${puzzleId}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching puzzle:', error);
    if (error.response?.data) return error.response.data;
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Fetch a puzzle instance by its instance UUID.
 *
 * @param {string} puzzleInstanceId - Puzzle instance UUID
 * @param {string|null} token - Optional Clerk JWT token for authentication
 */
export async function getPuzzleInstanceById(puzzleInstanceId, token = null) {
  try {
    const headers = {};
    const uid = getUserId();
    if (uid) headers['X-User-ID'] = uid;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await apiClient.get(`/puzzles/instances/${puzzleInstanceId}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching puzzle instance:', error);
    if (error.response?.data) return error.response.data;
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Fetch all versions for a puzzle family.
 *
 * @param {string} canonicalPuzzleId - Canonical puzzle UUID
 * @param {string|null} token - Optional Clerk JWT token for authentication
 */
export async function getPuzzleVersions(canonicalPuzzleId, token = null) {
  try {
    const headers = {};
    const uid = getUserId();
    if (uid) headers['X-User-ID'] = uid;
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await apiClient.get(`/puzzles/${canonicalPuzzleId}/versions`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching puzzle versions:', error);
    if (error.response?.data) return error.response.data;
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Create a new puzzle (first version of a new puzzle family) (admin only).
 *
 * @param {string} gameSlug - Game type slug
 * @param {object} config - Puzzle configuration
 * @param {string} token - Clerk JWT token (required)
 */
export async function createPuzzle(gameSlug, config, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.post('/puzzles', { 
      game_slug: gameSlug, 
      config 
    }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error creating puzzle:', error);
    if (error.response?.data) return error.response.data;
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Create a new version of an existing puzzle (admin only).
 *
 * @param {string} canonicalPuzzleId - Canonical puzzle UUID
 * @param {object} config - Updated puzzle config
 * @param {string} token - Clerk JWT token (required)
 */
export async function createPuzzleVersion(canonicalPuzzleId, config, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.post(`/puzzles/${canonicalPuzzleId}/versions`, { config }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error creating puzzle version:', error);
    if (error.response?.data) {
      // FastAPI returns errors as {"detail": "message"}
      const detail = error.response.data.detail || error.response.data.message;
      return { 
        success: false, 
        error: error.response.data.error || 'API_ERROR',
        message: detail,
        detail: detail
      };
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Schedule a specific puzzle version for a date (admin only).
 *
 * @param {string} puzzleId - Puzzle version UUID to schedule
 * @param {string} gameDate - Date to schedule for (YYYY-MM-DD format)
 * @param {boolean} replace - If true, replaces any existing puzzle for that game/date
 * @param {string} token - Clerk JWT token (required)
 * @returns {Promise<{
 *   success: boolean,
 *   puzzle_instance_id?: string,
 *   puzzle_id?: string,
 *   game_date?: string,
 *   replaced_existing?: boolean,
 *   error?: string,
 *   message?: string
 * }>}
 */
export async function schedulePuzzle(puzzleId, gameDate, replace, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.post(`/puzzles/${puzzleId}/schedule`, {
      game_date: gameDate,
      replace
    }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error scheduling puzzle:', error);
    if (error.response?.data) {
      const detail = error.response.data.detail || error.response.data.message;
      return { 
        success: false, 
        error: error.response.data.error || 'API_ERROR',
        message: detail,
        detail: detail
      };
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Bulk schedule multiple puzzles by calling schedulePuzzle sequentially.
 * 
 * @param {Array<{puzzleId: string, gameDate: string, replace?: boolean}>} schedules - Array of scheduling requests
 * @param {string} token - Clerk JWT token (required)
 * @param {Function} [onProgress] - Optional progress callback (completed, total, lastResult) => void
 * @returns {Promise<{success: boolean, results: Array<{puzzleId: string, gameDate: string, success: boolean, error?: string}>}>}
 */
export async function bulkSchedulePuzzles(schedules, token, onProgress = null) {
  const results = [];
  
  for (let i = 0; i < schedules.length; i++) {
    const { puzzleId, gameDate, replace = false } = schedules[i];
    
    try {
      const result = await schedulePuzzle(puzzleId, gameDate, replace, token);
      const itemResult = {
        puzzleId,
        gameDate,
        success: result.success,
        error: result.success ? undefined : (result.message || result.detail || 'Unknown error')
      };
      results.push(itemResult);
      
      if (onProgress) {
        onProgress(i + 1, schedules.length, itemResult);
      }
    } catch (error) {
      const itemResult = {
        puzzleId,
        gameDate,
        success: false,
        error: error.message || 'Network error'
      };
      results.push(itemResult);
      
      if (onProgress) {
        onProgress(i + 1, schedules.length, itemResult);
      }
    }
  }
  
  const successCount = results.filter(r => r.success).length;
  return {
    success: successCount === results.length,
    results,
    successCount,
    errorCount: results.length - successCount
  };
}

/**
 * Get the scheduled puzzle instance for a specific game and date.
 * Uses getPuzzleInstances with a single-day date filter.
 *
 * @param {string} gameSlug - Game type slug (e.g., 'cloze', 'scalinata')
 * @param {string} date - Date to check (YYYY-MM-DD format)
 * @param {string} token - Clerk JWT token (required)
 * @returns {Promise<{
 *   success: boolean,
 *   instance?: {
 *     game_slug: string,
 *     puzzle_date: string,
 *     puzzle_instance_id: string,
 *     puzzle_id: string,
 *     version: number
 *   } | null,
 *   error?: string,
 *   message?: string
 * }>}
 */
export async function getScheduledPuzzleForDate(gameSlug, date, token) {
  try {
    const result = await getPuzzleInstances({
      gameSlugs: [gameSlug],
      startDate: date,
      endDate: date
    }, token);
    
    if (!result.success) {
      return result;
    }
    
    // Find the instance for this specific game/date
    const instance = result.instances?.find(
      inst => inst.game_slug === gameSlug && inst.puzzle_date === date
    ) || null;
    
    return { success: true, instance };
  } catch (error) {
    console.error('Error getting scheduled puzzle for date:', error);
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Generate a complete Ortografia config from letters (admin only).
 *
 * @param {string[]} letters - Array of 7 letters
 * @param {string} centerLetter - Center letter (must be in letters)
 * @param {string} token - Clerk JWT token (required)
 * @param {Array<{word: string, level: string}>|null} wordList - Optional word list for editing mode
 * @returns {Promise<{success: boolean, config?: object, error?: string, message?: string}>}
 */
export async function generateOrtografiaConfig(letters, centerLetter, token, wordList = null) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const payload = {
      letters,
      center_letter: centerLetter,
    };
    
    if (wordList) {
      payload.word_list = wordList;
    }
    
    const response = await apiClient.post('/puzzles/ortografia/generate', payload, { headers });
    return response.data;
  } catch (error) {
    console.error('Error generating Ortografia config:', error);
    if (error.response?.data) {
      // FastAPI returns errors as {"detail": "message"}
      const detail = error.response.data.detail || error.response.data.message;
      return { 
        success: false, 
        error: error.response.data.error || 'API_ERROR',
        message: detail,
        detail: detail
      };
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Generate a complete Serpentoni config from manual entries (admin only).
 *
 * @param {string} theme - Theme label for the puzzle
 * @param {string} prompt - Prompt shown to players (max 50 chars)
 * @param {string} spangram - Spangram text
 * @param {string[]} words - Theme words (5-8 entries)
 * @param {string} token - Clerk JWT token (required)
 * @returns {Promise<{success: boolean, config?: object, error?: string, message?: string}>}
 */
export async function generateSerpentoniConfig(theme, prompt, spangram, words, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const timeoutMs = 15000;
    const response = await apiClient.post('/puzzles/serpentoni/generate', {
      theme,
      prompt,
      spangram,
      words,
    }, { headers, timeout: timeoutMs });
    return response.data;
  } catch (error) {
    console.error('Error generating Serpentoni config:', error);
    if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        error: 'TIMEOUT',
        message: 'La generazione ha impiegato troppo tempo. Riprova con parole diverse.',
        detail: 'Serpentoni generation request timed out',
      };
    }
    if (error.response?.data) {
      const rawDetail = error.response.data.detail || error.response.data.message;
      let detail = rawDetail;

      if (rawDetail && typeof rawDetail === 'object' && rawDetail.message) {
        const localized = rawDetail.message;
        detail = localized.it || localized.en || error.response.data.message || 'Errore durante la generazione di Serpentoni';
      }

      return {
        success: false,
        error: error.response.data.error || rawDetail?.error || 'API_ERROR',
        message: detail,
        detail,
      };
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Delete a puzzle instance (unschedule a puzzle from a date) (admin only).
 * 
 * Only allows deletion if there are no gameplay sessions.
 *
 * @param {string} instanceId - Puzzle instance UUID to delete
 * @param {string} token - Clerk JWT token (required)
 * @returns {Promise<{
 *   success: boolean,
 *   instance_id?: string,
 *   game_slug?: string,
 *   game_date?: string,
 *   session_count?: number,
 *   was_active?: boolean,
 *   error?: string,
 *   message?: string
 * }>}
 */
export async function deletePuzzleInstance(instanceId, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.delete(`/puzzles/instances/${instanceId}`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error deleting puzzle instance:', error);
    if (error.response?.data) {
      const detail = error.response.data.detail || error.response.data.message;
      return { 
        success: false, 
        error: error.response.data.error || 'API_ERROR',
        message: detail,
        detail: detail
      };
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

function normalizeSponsorshipApiError(data) {
  const rawDetail = data?.detail ?? data?.message;

  if (Array.isArray(rawDetail)) {
    const messages = rawDetail
      .map((entry) => (typeof entry?.msg === 'string' ? entry.msg : ''))
      .filter(Boolean);
    return {
      success: false,
      error: data?.error || 'API_ERROR',
      message: messages.join('; ') || 'Validation error',
      detail: rawDetail,
    };
  }

  if (typeof rawDetail === 'string') {
    return {
      success: false,
      error: data?.error || 'API_ERROR',
      message: rawDetail,
      detail: rawDetail,
    };
  }

  return {
    success: false,
    error: data?.error || 'API_ERROR',
    message: 'Errore del server',
    detail: rawDetail ?? null,
  };
}

/**
 * Get all puzzle sponsorship summaries for admin management.
 *
 * @param {string} token - Clerk JWT token (required)
 */
export async function getPuzzleSponsorships(token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.get('/puzzles/sponsorships', { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching puzzle sponsorships:', error);
    if (error.response?.data) {
      return normalizeSponsorshipApiError(error.response.data);
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Get sponsorship details for a specific puzzle instance (admin only).
 *
 * @param {string} instanceId - Puzzle instance UUID
 * @param {string} token - Clerk JWT token (required)
 */
export async function getPuzzleInstanceSponsorship(instanceId, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.get(`/puzzles/instances/${instanceId}/sponsorship`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error fetching puzzle instance sponsorship:', error);
    if (error.response?.data) {
      return normalizeSponsorshipApiError(error.response.data);
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Create or update sponsorship details for a specific puzzle instance (admin only).
 *
 * @param {string} instanceId - Puzzle instance UUID
 * @param {object} payload - Sponsorship payload
 * @param {string} token - Clerk JWT token (required)
 */
export async function upsertPuzzleInstanceSponsorship(instanceId, payload, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.put(`/puzzles/instances/${instanceId}/sponsorship`, payload, { headers });
    return response.data;
  } catch (error) {
    console.error('Error saving puzzle instance sponsorship:', error);
    if (error.response?.data) {
      return normalizeSponsorshipApiError(error.response.data);
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Update only the active/inactive status for a specific sponsorship (admin only).
 *
 * @param {string} instanceId - Puzzle instance UUID
 * @param {'active'|'inactive'} status - Next sponsorship status
 * @param {string} token - Clerk JWT token (required)
 */
export async function updatePuzzleInstanceSponsorshipStatus(instanceId, status, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.patch(`/puzzles/instances/${instanceId}/sponsorship/status`, { status }, { headers });
    return response.data;
  } catch (error) {
    console.error('Error updating puzzle instance sponsorship status:', error);
    if (error.response?.data) {
      return normalizeSponsorshipApiError(error.response.data);
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}

/**
 * Delete sponsorship details for a specific puzzle instance (admin only).
 *
 * @param {string} instanceId - Puzzle instance UUID
 * @param {string} token - Clerk JWT token (required)
 */
export async function deletePuzzleInstanceSponsorship(instanceId, token) {
  try {
    const headers = { 'Authorization': `Bearer ${token}` };
    const response = await apiClient.delete(`/puzzles/instances/${instanceId}/sponsorship`, { headers });
    return response.data;
  } catch (error) {
    console.error('Error deleting puzzle instance sponsorship:', error);
    if (error.response?.data) {
      return normalizeSponsorshipApiError(error.response.data);
    }
    return { success: false, error: 'NETWORK_ERROR', message: 'Errore di connessione' };
  }
}
